/**
 * PodMatrixDefaultsSelector — mapping atelier `{finish_key:
 * printable_spec_id}` consommé par `auto_create_for_work` pour router
 * chaque finition d'une nouvelle matrice POD vers son productUid Gelato
 * dédié (Plan B 2026-05-18).
 *
 * Composant Settings → Catalogue POD. Liste les finitions actives
 * (`no_frame` + chaque framing actif) et pour chacune, propose un
 * `<Select>` parmi les `printable_specs` Gelato actives. Sauvegarde
 * persiste le mapping dans `app_settings.pod_matrix_default_spec_by_finish`.
 *
 * Sémantique :
 *   - Setting absent ou clé manquante = fallback "même spec partout"
 *     côté `auto_create_for_work` (rétro-compat). UI indique
 *     "(non configuré)" en option vide.
 *   - Mapping vide submit = clear le setting (retour au fallback).
 *
 * Pas de réordonnancement — l'ordre vient des framings catalog
 * (sorted active). `no_frame` toujours en tête.
 */

import { useState } from 'react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import Select from '@/components/ui/Select'
import { useFramings } from '@/hooks/useCatalog'
import { usePrintableSpecsPicker } from '@/hooks/usePrintableSpecs'
import {
  usePodMatrixDefaultSpecByFinish,
  useUpdatePodMatrixDefaultSpecByFinish,
} from '@/hooks/useAtelierSettings'

import './PodMatrixDefaultsSelector.css'

const NO_FRAME_KEY = 'no_frame'
const NO_FRAME_LABEL = 'Sans cadre (poster)'

function finishKey(framingId: number | null): string {
  return framingId === null ? NO_FRAME_KEY : `framing_${framingId}`
}

export default function PodMatrixDefaultsSelector() {
  const settingQuery = usePodMatrixDefaultSpecByFinish()
  const framingsQuery = useFramings({ only_active: true })
  const specsQuery = usePrintableSpecsPicker('gelato')
  const updateMutation = useUpdatePodMatrixDefaultSpecByFinish()

  const serverData = settingQuery.data
  const framings = framingsQuery.data ?? []
  const specs = specsQuery.data ?? []

  // Liste des finitions à afficher : no_frame + chaque framing actif
  // (l'ordre vient du catalogue). Chaque finish = une ligne avec son select.
  const finishRows: Array<{ key: string; label: string; labelEn: string | null }> = [
    { key: NO_FRAME_KEY, label: NO_FRAME_LABEL, labelEn: 'No frame' },
    ...framings.map((f) => ({
      key: finishKey(f.id),
      label: f.label_fr || f.label_en || `Cadre #${f.id}`,
      labelEn: f.label_en ?? null,
    })),
  ]

  // Représentation canonique (clés triées) — la comparaison ordre-sensible
  // de `JSON.stringify` introduit des resets/dirty erronés quand BE et FE
  // construisent l'objet dans des ordres différents.
  const canonicalize = (obj: Record<string, number>): string => {
    const keys = Object.keys(obj).sort()
    return JSON.stringify(keys.map((k) => [k, obj[k]]))
  }

  // État local synchronisé sur server data (pattern adjust-state-on-prop).
  const computeInitial = (): Record<string, number | null> => {
    if (!serverData) return {}
    const initial: Record<string, number | null> = {}
    for (const row of finishRows) {
      initial[row.key] = serverData.value[row.key] ?? null
    }
    return initial
  }

  const [mapping, setMapping] = useState<Record<string, number | null>>(computeInitial)
  const [serverSnapshot, setServerSnapshot] = useState<string | null>(
    serverData ? canonicalize(serverData.value) : null,
  )
  if (serverData) {
    const next = canonicalize(serverData.value)
    if (next !== serverSnapshot) {
      setServerSnapshot(next)
      setMapping(computeInitial())
    }
  }

  const handleChange = (key: string, value: string) => {
    const specId = value === '' ? null : Number(value)
    setMapping((prev) => ({ ...prev, [key]: specId }))
  }

  const handleSave = () => {
    // Compose le payload : retire les clés non-mappées (null) — le BE
    // accepte un mapping partiel, les finishes restantes tombent sur
    // le fallback.
    const payload: Record<string, number> = {}
    for (const [key, value] of Object.entries(mapping)) {
      if (value !== null) payload[key] = value
    }
    updateMutation.mutate(
      { value: payload },
      {
        onSuccess: () => {
          toast.success('Mapping atelier sauvegardé.')
        },
        onError: (err) => {
          const detailObj = err.detail as string | undefined
          toast.error(detailObj ?? 'Sauvegarde impossible.')
        },
      },
    )
  }

  const isLoading = settingQuery.isLoading || framingsQuery.isLoading || specsQuery.isLoading
  const hasNoSpecs = !isLoading && specs.length === 0
  const currentNonNull = Object.fromEntries(
    Object.entries(mapping).filter(([, v]) => v !== null) as [string, number][],
  )
  const isDirty = !!serverData && canonicalize(currentNonNull) !== canonicalize(serverData.value)

  return (
    <section className="pod-matrix-defaults-selector">
      <header className="pod-matrix-defaults-selector-header">
        <h3 className="ui-h5">Defaults productUid Gelato par finition</h3>
        <p className="ui-body-sm ui-text-muted">
          Pour chaque finition active du catalogue, choisis le <code>printable_spec</code> Gelato
          dédié. Chaque nouvelle œuvre POD prendra ces defaults — Plan B bi-axe. Les œuvres
          existantes ne sont pas impactées (re-publie pour appliquer). Sans mapping, toutes les
          finitions tombent sur la 1ère spec active du catalogue (fallback rétro-compat).
        </p>
      </header>

      {isLoading ? (
        <p className="ui-body ui-text-muted" role="status">
          Chargement…
        </p>
      ) : hasNoSpecs ? (
        <p className="ui-body ui-text-muted">
          Aucune <code>printable_spec</code> Gelato active. Crée d&apos;abord des configs via le
          bouton « Nouvelle config » en haut de la page.
        </p>
      ) : (
        <>
          <ul className="pod-matrix-defaults-selector-list">
            {finishRows.map((row) => (
              <li key={row.key} className="pod-matrix-defaults-selector-row">
                <div className="pod-matrix-defaults-selector-row-label">
                  <strong>{row.label}</strong>
                  {row.labelEn && row.labelEn !== row.label ? (
                    <span className="ui-text-muted ui-body-sm">{row.labelEn}</span>
                  ) : null}
                  <code className="ui-body-sm ui-text-muted">{row.key}</code>
                </div>
                <Select
                  value={
                    mapping[row.key] !== null && mapping[row.key] !== undefined
                      ? String(mapping[row.key])
                      : ''
                  }
                  onChange={(e) => handleChange(row.key, e.target.value)}
                  aria-label={`Spec pour ${row.label}`}
                >
                  <option value="">(non configuré — fallback)</option>
                  {specs.map((spec) => (
                    <option key={spec.id} value={spec.id}>
                      {spec.label_cache} ({spec.sku})
                    </option>
                  ))}
                </Select>
              </li>
            ))}
          </ul>

          <div className="pod-matrix-defaults-selector-actions">
            <Button
              variant="primary"
              size="md"
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Sauvegarde…' : 'Enregistrer'}
            </Button>
          </div>
        </>
      )}
    </section>
  )
}
